/**
 * PricingCalculatorFormulas
 *
 * A user-friendly transparency panel that shows how pricing is calculated.
 * Uses visual cards and clear explanations to help sales reps understand
 * and explain pricing to customers.
 *
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Pricing constants
 */

import React, { useState } from 'react';
import { Box, Typography, Chip, alpha, useTheme, Tooltip, Theme } from '@mui/material';
import {
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  Users as UsersIcon,
  CreditCard as CreditCardIcon,
  Server as ServerIcon,
  Blocks as BlocksIcon,
  HelpCircle as HelpIcon,
  Check as CheckIcon,
  X as XIcon,
  ArrowRight as ArrowRightIcon,
  Link2 as LinkIcon,
} from 'lucide-react';
import {
  PricingCalculatorFormValues,
  PricingCalculatorSummary,
  PlanType,
  isDedicatedTenant,
  calculateIntegrationSetupFee,
  calculateIntegrationRecurringFee,
  calculatePlatformFee,
  getPlatformFeeTierMinUsers,
  INTEGRATION_METADATA,
  PLATFORM_FEE_TIER_TABLE,
  PRICING_CONSTANTS,
} from '../../types/pricingCalculator';
import { pricingCalculatorIntegrationsHash } from '../../hooks/usePricingCalculatorIntegrationsMultiSelector';

// Format currency helper
const formatCurrency = (amount: number) =>
  `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * How many of the published platform-fee tiers get their own segment in the
 * compact tier bar. Everything above them collapses into one trailing segment —
 * the full 26-tier scale lives in the Tier card (see TierIndicator).
 */
const PLATFORM_TIER_BAR_ROWS = 6;

/** Shown instead of a price when the active plan is not sold at that tier's size. */
const PLATFORM_TIER_UNAVAILABLE = 'n/a';

// Formula Card Component - Shows one pricing formula with visual breakdown
interface FormulaCardProps {
  title: string;
  icon: React.ReactNode;
  isActive: boolean;
  formula: React.ReactNode;
  steps: Array<{
    label: string;
    value: string;
    highlight?: boolean;
  }>;
  result: string;
  note?: string;
  children?: React.ReactNode;
  /** Controlled expanded state (optional - uses internal state if not provided) */
  expanded?: boolean;
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Default expanded state for internal state (defaults to true for active, false for inactive) */
  defaultExpanded?: boolean;
}

/** Controlled-or-internal expanded state plus the click/keyboard toggles both headers share. */
const useExpandableHeader = (
  defaultExpanded: boolean,
  controlledExpanded: boolean | undefined,
  onExpandedChange: ((expanded: boolean) => void) | undefined,
) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const newExpanded = !expanded;
    if (isControlled && onExpandedChange) {
      onExpandedChange(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return { expanded, handleToggle, handleKeyDown };
};

const FormulaCard: React.FC<FormulaCardProps> = ({
  title,
  icon,
  isActive,
  formula,
  steps,
  result,
  note,
  children,
  expanded: controlledExpanded,
  onExpandedChange,
  defaultExpanded,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Default to collapsed for inactive items, expanded for active items
  const { expanded, handleToggle, handleKeyDown } = useExpandableHeader(
    defaultExpanded ?? isActive,
    controlledExpanded,
    onExpandedChange,
  );

  // Stable id derived from the title so `aria-controls` always points at the
  // expanded-content element below.
  const contentId = `formula-card-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: isActive ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider,
        backgroundColor: isActive
          ? isDark
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.primary.main, 0.02)
          : isDark
            ? alpha(theme.palette.grey[800], 0.3)
            : alpha(theme.palette.grey[100], 0.5),
        overflow: 'hidden',
        opacity: isActive ? 1 : 0.6,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Header */}
      <Box
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              display: 'flex',
            }}
          >
            {icon}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {!isActive && (
            <Chip
              icon={<XIcon size={12} />}
              label="Not Active"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: isActive ? theme.palette.success.main : theme.palette.text.secondary,
            }}
          >
            {result}
          </Typography>
          {expanded ? <ChevronDownIcon size={18} /> : <ChevronRightIcon size={18} />}
        </Box>
      </Box>

      {/* Container is always rendered with the id targeted by `aria-controls`;
       * visibility is toggled via `hidden` (no animated transition, so we
       * still avoid the MUI Collapse height-recalc fight). See
       * PricingCalculatorSection for the library-bug context. */}
      <Box
        id={contentId}
        hidden={!expanded}
        sx={{
          px: 2,
          pb: 2,
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
        }}
      >
        {/* Formula Display */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            backgroundColor: isDark
              ? alpha(theme.palette.grey[900], 0.5)
              : alpha(theme.palette.grey[100], 0.8),
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: theme.palette.text.secondary,
          }}
        >
          {formula}
        </Box>

        {/* Step-by-step breakdown */}
        <Box sx={{ mt: 2 }}>
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.75,
                borderBottom:
                  index < steps.length - 1 ? `1px dashed ${theme.palette.divider}` : 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {index < steps.length - 1 ? (
                  <ArrowRightIcon size={14} color={theme.palette.text.secondary} />
                ) : (
                  <CheckIcon size={14} color={theme.palette.success.main} />
                )}
                <Typography variant="body2" color="text.secondary">
                  {step.label}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: step.highlight ? 600 : 400,
                  color: step.highlight ? theme.palette.primary.main : theme.palette.text.primary,
                  fontFamily: 'monospace',
                }}
              >
                {step.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Note */}
        {note && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <HelpIcon size={16} color={theme.palette.info.main} style={{ marginTop: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {note}
            </Typography>
          </Box>
        )}

        {children}
      </Box>
    </Box>
  );
};

// Tier Visual Component - Shows which tier the user is in
interface TierVisualProps {
  title: string;
  tiers: Array<{
    label: string;
    range: string;
    value: string;
  }>;
  currentTierIndex: number;
}

const TierVisual: React.FC<TierVisualProps> = ({ title, tiers, currentTierIndex }) => {
  const theme = useTheme();

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {tiers.map((tier, index) => (
          <Tooltip key={index} title={`${tier.range}: ${tier.value}`} arrow>
            <Box
              sx={{
                flex: 1,
                height: 8,
                borderRadius: '4px',
                backgroundColor:
                  index === currentTierIndex
                    ? theme.palette.primary.main
                    : index < currentTierIndex
                      ? alpha(theme.palette.primary.main, 0.3)
                      : alpha(theme.palette.grey[500], 0.2),
                cursor: 'pointer',
                transition: 'transform 0.15s ease',
                '&:hover': {
                  transform: 'scaleY(1.5)',
                },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {tiers[0]?.label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          You: Tier {currentTierIndex + 1}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tiers[tiers.length - 1]?.label}
        </Typography>
      </Box>
    </Box>
  );
};

// Category Section Component
interface CategorySectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  /** Controlled expanded state (optional - uses internal state if not provided) */
  expanded?: boolean;
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  title,
  icon,
  children,
  expanded: controlledExpanded,
  onExpandedChange,
}) => {
  const theme = useTheme();
  const { expanded, handleToggle, handleKeyDown } = useExpandableHeader(
    true,
    controlledExpanded,
    onExpandedChange,
  );

  const contentId = `category-section-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
            borderRadius: '4px',
          },
        }}
      >
        <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          {title}
        </Typography>
        {expanded ? <ChevronDownIcon size={18} /> : <ChevronRightIcon size={18} />}
      </Box>
      <Box
        id={contentId}
        hidden={!expanded}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {children}
      </Box>
    </Box>
  );
};

export interface PricingCalculatorFormulasProps {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary | null;
  /** Original integrations (for renewal handling) */
  originalIntegrations?: string[];
  /** Expected accounts per integration */
  integrationPipelineOpportunities?: Record<string, number>;
  /** Setup discount per integration */
  integrationSetupDiscounts?: Record<string, number>;
  /** Recurring discount per integration */
  integrationRecurringDiscounts?: Record<string, number>;
  /** Migration flag per integration */
  integrationMigrations?: Record<string, boolean>;
  /** Controlled all-expanded state (optional) */
  allExpanded?: boolean;
  /** Callback when expand state changes internally */
  onAllExpandedChange?: (allExpanded: boolean) => void;
}

type AddonsSectionProps = {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary | null;
  theme: Theme;
  ssoTiers: Array<{ label: string; range: string; value: string }>;
  getSsoTierIndex: (members: number) => number;
  discountedPrice: number;
  memberCostTotal: number;
  expanded: boolean;
  onExpandedChange: () => void;
};

/**
 * Add-ons formula cards. Extracted from PricingCalculatorFormulas to keep both
 * functions under the repo complexity ceilings; rendering is unchanged.
 */
const AddonsSection: React.FC<AddonsSectionProps> = ({
  values,
  summary,
  theme,
  ssoTiers,
  getSsoTierIndex,
  discountedPrice,
  memberCostTotal,
  expanded,
  onExpandedChange,
}) => (
  <CategorySection
    title="Add-ons"
    icon={<BlocksIcon size={18} />}
    expanded={expanded}
    onExpandedChange={onExpandedChange}
  >
    <FormulaCard
      title="Security Package (SSO)"
      icon={<BlocksIcon size={18} />}
      isActive={values.securityPackageSso}
      formula={
        <Box>
          <span style={{ color: theme.palette.primary.main }}>Members</span> × Tier Rate ×{' '}
          <span style={{ color: theme.palette.info.main }}>Duration</span>
        </Box>
      }
      steps={[
        { label: 'Number of members', value: `${values.numberOfMembers}` },
        {
          label: `SSO rate (Tier ${getSsoTierIndex(values.numberOfMembers) + 1})`,
          value: ssoTiers[getSsoTierIndex(values.numberOfMembers)].value,
        },
        {
          label: `Total (${values.subscriptionDuration} months)`,
          value: formatCurrency(summary?.ssoTotalYearly || 0),
          highlight: true,
        },
      ]}
      result={formatCurrency(summary?.ssoTotalYearly || 0)}
    >
      <TierVisual
        title="Your SSO Tier (more members = lower per-user rate)"
        tiers={ssoTiers}
        currentTierIndex={getSsoTierIndex(values.numberOfMembers)}
      />
    </FormulaCard>

    <FormulaCard
      title="AI Forecasting"
      icon={<BlocksIcon size={18} />}
      isActive={values.aiForecasting}
      formula={<Box>35% × (Member License Cost + Integration Recurring Fees)</Box>}
      steps={[
        { label: 'Base calculation', value: '35% of applicable fees' },
        {
          label: `After discount (${values.discountOnAiForecasting}%)`,
          value: formatCurrency(summary?.aiForecastingFeeAfterDiscount || 0),
          highlight: true,
        },
      ]}
      result={formatCurrency(summary?.aiForecastingFeeAfterDiscount || 0)}
    />

    <FormulaCard
      title="Non-People Resources"
      icon={<BlocksIcon size={18} />}
      isActive={values.nonPeopleResources}
      formula={<Box>50% × Discounted License Price × Members × Duration</Box>}
      steps={[
        { label: 'Discounted price', value: formatCurrency(discountedPrice) },
        { label: 'Multiplier', value: '50%' },
        {
          label: `Total (${values.numberOfMembers} × ${values.subscriptionDuration} mo)`,
          value: formatCurrency(summary?.nonPeopleResourcesFeeAfterDiscount || 0),
          highlight: true,
        },
      ]}
      result={formatCurrency(summary?.nonPeopleResourcesFeeAfterDiscount || 0)}
    />

    <FormulaCard
      title="Profile Building"
      icon={<BlocksIcon size={18} />}
      isActive={values.profileBuilding}
      formula={<Box>$10 × Members × Duration</Box>}
      steps={[
        { label: 'Rate per member', value: '$10/month' },
        {
          label: `Total (${values.numberOfMembers} × ${values.subscriptionDuration} mo)`,
          value: formatCurrency(summary?.profileBuildingFeeAfterDiscount || 0),
          highlight: true,
        },
      ]}
      result={formatCurrency(summary?.profileBuildingFeeAfterDiscount || 0)}
    />

    <FormulaCard
      title="Skills Tagging"
      icon={<BlocksIcon size={18} />}
      isActive={values.skillsTagging}
      formula={<Box>$5 × Members × Duration</Box>}
      steps={[
        { label: 'Rate per member', value: '$5/month' },
        {
          label: `Total (${values.numberOfMembers} × ${values.subscriptionDuration} mo)`,
          value: formatCurrency(summary?.skillsTaggingFeeAfterDiscount || 0),
          highlight: true,
        },
      ]}
      result={formatCurrency(summary?.skillsTaggingFeeAfterDiscount || 0)}
    />

    <FormulaCard
      title="White-Label"
      icon={<BlocksIcon size={18} />}
      isActive={values.enableWhiteLabel}
      formula={<Box>20% × Member License Cost</Box>}
      steps={[
        { label: 'Member cost', value: formatCurrency(memberCostTotal) },
        { label: 'White-label surcharge', value: '20%' },
        {
          label: 'Additional cost',
          value: formatCurrency(summary?.whiteLabelAdditionalCost || 0),
          highlight: true,
        },
      ]}
      result={formatCurrency(summary?.whiteLabelAdditionalCost || 0)}
    />
  </CategorySection>
);

type ServerUpgradesSectionPropsType = {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary | null;
  theme: Theme;
  isolatedDbTiers: Array<{ label: string; range: string; value: string }>;
  getIsolatedDbTierIndex: (members: number) => number;
  expanded: boolean;
  onExpandedChange: () => void;
};

/** Server-upgrade formula cards (direct/isolated DB, dedicated tenant, SSO, gov cloud). */
const ServerUpgradesSection = ({
  values,
  summary,
  theme,
  isolatedDbTiers,
  getIsolatedDbTierIndex,
  expanded,
  onExpandedChange,
}: ServerUpgradesSectionPropsType) => (
  <>
    <CategorySection
      title="Server Upgrades"
      icon={<ServerIcon size={18} />}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <FormulaCard
        title="Isolated Database"
        icon={<ServerIcon size={18} />}
        isActive={
          values.isolatedDatabase &&
          !values.directDatabaseIntegration &&
          !isDedicatedTenant(values.tenantType)
        }
        formula={
          <Box>
            Tier-based monthly fee ×{' '}
            <span style={{ color: theme.palette.info.main }}>Duration</span>
          </Box>
        }
        steps={[
          {
            label: `Your tier (${values.numberOfMembers} members)`,
            value: isolatedDbTiers[getIsolatedDbTierIndex(values.numberOfMembers)].value,
          },
          {
            label: `Total (${values.subscriptionDuration} months)`,
            value: formatCurrency(summary?.isolatedDatabaseFeeYearly || 0),
            highlight: true,
          },
        ]}
        result={formatCurrency(summary?.isolatedDatabaseFeeYearly || 0)}
        note={
          values.directDatabaseIntegration
            ? 'Included with Direct Database Integration'
            : isDedicatedTenant(values.tenantType)
              ? 'Included with dedicated tenant'
              : undefined
        }
      >
        <TierVisual
          title="Your Isolated Database Tier"
          tiers={isolatedDbTiers}
          currentTierIndex={getIsolatedDbTierIndex(values.numberOfMembers)}
        />
      </FormulaCard>

      <FormulaCard
        title="Direct Database Integration"
        icon={<ServerIcon size={18} />}
        isActive={values.directDatabaseIntegration}
        formula={
          <Box>
            MAX($2,000, Members × $50) ×{' '}
            <span style={{ color: theme.palette.info.main }}>Duration</span>
          </Box>
        }
        steps={[
          {
            label: 'Calculated fee',
            value: `${values.numberOfMembers} × $50 = ${formatCurrency(values.numberOfMembers * 50)}`,
          },
          { label: 'Minimum floor', value: '$2,000' },
          {
            label: 'Applied fee',
            value: formatCurrency(Math.max(2000, values.numberOfMembers * 50)),
          },
          {
            label: `Total (${values.subscriptionDuration} months)`,
            value: formatCurrency(summary?.directDatabaseFee || 0),
            highlight: true,
          },
        ]}
        result={formatCurrency(summary?.directDatabaseFee || 0)}
        note="Direct Database automatically includes Isolated Database at no additional cost"
      />

      <FormulaCard
        title="Gov Cloud (FedRAMP)"
        icon={<ServerIcon size={18} />}
        isActive={values.govCloud}
        formula={<Box>20% × (Member Cost + SSO + Database Fees + Integration Fees)</Box>}
        steps={[
          { label: 'Surcharge rate', value: '20%' },
          { label: 'Applied to', value: 'Members, SSO, DB, Integrations' },
          {
            label: 'Gov Cloud fee',
            value: formatCurrency(summary?.govCloudFeeAfterDiscount || 0),
            highlight: true,
          },
        ]}
        result={formatCurrency(summary?.govCloudFeeAfterDiscount || 0)}
        note="FedRAMP compliant hosting adds 20% surcharge to applicable fees"
      />
    </CategorySection>
  </>
);

export const PricingCalculatorFormulas: React.FC<PricingCalculatorFormulasProps> = ({
  values,
  summary,
  originalIntegrations,
  integrationPipelineOpportunities = {},
  integrationSetupDiscounts = {},
  integrationRecurringDiscounts = {},
  integrationMigrations = {},
  allExpanded: controlledAllExpanded,
  onAllExpandedChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Track if the last change was from global toggle (to prevent feedback loop)
  const isGlobalToggleRef = React.useRef(false);

  // Individual section expand states
  const [sectionStates, setSectionStates] = useState({
    memberPricing: true,
    platformOnboarding: true,
    serverUpgrades: true,
    addons: true,
    integrations: true,
  });

  // Sync with controlled state when it changes (only from global toggle)
  React.useEffect(() => {
    if (controlledAllExpanded !== undefined) {
      isGlobalToggleRef.current = true;
      const newState = {
        memberPricing: controlledAllExpanded,
        platformOnboarding: controlledAllExpanded,
        serverUpgrades: controlledAllExpanded,
        addons: controlledAllExpanded,
        integrations: controlledAllExpanded,
      };
      setSectionStates(newState);
      // Reset flag after state update
      requestAnimationFrame(() => {
        isGlobalToggleRef.current = false;
      });
    }
  }, [controlledAllExpanded]);

  const toggleSection = (section: keyof typeof sectionStates) => {
    setSectionStates((prev) => {
      const newState = { ...prev, [section]: !prev[section] };
      // Notify parent about the new all-expanded state
      if (onAllExpandedChange) {
        const allExpanded = Object.values(newState).every(Boolean);
        onAllExpandedChange(allExpanded);
      }
      return newState;
    });
  };

  // Calculate values for display
  const basePrice = values.pricePerMember;
  const discountedPrice = basePrice * (1 - values.discountOnMemberPrice / 100);
  const memberCostMonthly = values.numberOfMembers * discountedPrice;
  const memberCostTotal = memberCostMonthly * values.subscriptionDuration;

  // Tier calculations. The platform-fee bar shows the first
  // PLATFORM_TIER_BAR_ROWS published tiers and folds everything above them into
  // one final segment, so the index has to clamp the same way.
  const getPlatformTierIndex = (members: number) => {
    const index = PLATFORM_FEE_TIER_TABLE.findIndex((tier) => members <= tier.maxUsers);
    return index === -1 ? PLATFORM_TIER_BAR_ROWS : Math.min(index, PLATFORM_TIER_BAR_ROWS);
  };

  // SSO tier index based on calculateSsoFeePerUser tiers
  const getSsoTierIndex = (members: number) => {
    if (members <= 49) return 0;
    if (members <= 99) return 1;
    if (members <= 249) return 2;
    if (members <= 499) return 3;
    if (members <= 999) return 4;
    return 5; // 1000+
  };

  // Isolated DB tier index based on calculateIsolatedDatabaseFee formula
  // $100/mo base, +$0.50/user/mo over 100 users
  const getIsolatedDbTierIndex = (members: number) => {
    if (members <= 100) return 0;
    if (members <= 200) return 1;
    if (members <= 500) return 2;
    return 3; // 500+
  };

  // Platform tier values, sampled from the published scale for the plan in
  // effect so the bar can never drift from the Platform Fee field (GLX-1931).
  // The scale has 26 published tiers; the bar shows the first few and folds the
  // rest into a trailing segment, which is why the last row reads "from $X".
  //
  // Where a tier is sampled matters, because two plan rules cut across the tier
  // boundaries rather than following them: Base is a flat $999 at or
  // below 15 members, and Enterprise is not sold below 200. Sampling a tier at
  // its lower bound would therefore report $999 for the whole 1-25 segment on
  // Base, and $0 for the 152-201 segment on Enterprise. So each tier is sampled
  // at its UPPER bound, which clears both thresholds — except the tier the
  // customer is actually in, which is sampled at their own member count so the
  // bar always agrees with the Platform Fee field.
  const platformTierValue = (members: number) => {
    const { platformFee } = calculatePlatformFee(members, values.planType);
    // Enterprise has no price at all below 200 members; "$0.00/yr" would read
    // as free rather than unavailable.
    return platformFee > 0 ? `${formatCurrency(platformFee)}/yr` : PLATFORM_TIER_UNAVAILABLE;
  };

  const currentPlatformTierIndex = getPlatformTierIndex(values.numberOfMembers);
  const platformOverflowMinUsers = getPlatformFeeTierMinUsers(PLATFORM_TIER_BAR_ROWS);

  // The trailing segment collapses every tier past the ones drawn above, so it
  // normally quotes an entry price ("from $X"). When it is the customer's own
  // segment that would be badly wrong — a 3,501-member Standard deal would read
  // "from $13,514/yr" next to a Platform Fee field showing $74,324/yr — so it
  // switches to their exact fee, same rule the drawn segments use.
  const isOnOverflowTier = currentPlatformTierIndex === PLATFORM_TIER_BAR_ROWS;
  const platformOverflowValue = platformTierValue(
    isOnOverflowTier ? values.numberOfMembers : platformOverflowMinUsers,
  );

  const platformTiers = [
    ...PLATFORM_FEE_TIER_TABLE.slice(0, PLATFORM_TIER_BAR_ROWS).map((tier, index) => {
      const minUsers = getPlatformFeeTierMinUsers(index);
      return {
        label: `${minUsers}-${tier.maxUsers}`,
        range: `${minUsers}-${tier.maxUsers} members`,
        value: platformTierValue(
          index === currentPlatformTierIndex ? values.numberOfMembers : tier.maxUsers,
        ),
      };
    }),
    {
      label: `${platformOverflowMinUsers}+`,
      range: `${platformOverflowMinUsers}+ members`,
      value:
        platformOverflowValue === PLATFORM_TIER_UNAVAILABLE || isOnOverflowTier
          ? platformOverflowValue
          : `from ${platformOverflowValue}`,
    },
  ];

  // SSO tier values based on calculateSsoFeePerUser function
  const ssoTiers = [
    { label: '1-49', range: '1-49 members', value: '$0.50/user/mo' },
    { label: '50-99', range: '50-99 members', value: '$0.40/user/mo' },
    { label: '100-249', range: '100-249 members', value: '$0.38/user/mo' },
    { label: '250-499', range: '250-499 members', value: '$0.30/user/mo' },
    { label: '500-999', range: '500-999 members', value: '$0.25/user/mo' },
    { label: '1000+', range: '1000+ members', value: '$0.23/user/mo' },
  ];

  // Isolated DB tier values based on calculateIsolatedDatabaseFee function
  // Formula: $100/mo base, +$0.50/user/mo over 100 users
  const isolatedDbTiers = [
    { label: '1-100', range: '1-100 members', value: '$100/mo' },
    { label: '101-200', range: '101-200 members', value: '$100 + $0.50/user over 100' },
    { label: '201-500', range: '201-500 members', value: '$100 + $0.50/user over 100' },
    { label: '500+', range: '500+ members', value: '$100 + $0.50/user over 100' },
  ];

  // Calculate integration details
  const getIntegrationDetails = () => {
    if (values.selectedIntegrations.length === 0) return [];

    const is4xDataTransfer = values.enable4xDataTransfer;

    return values.selectedIntegrations.map((integrationValue) => {
      const integration = pricingCalculatorIntegrationsHash[integrationValue];
      const metadata = INTEGRATION_METADATA[integrationValue];
      const isFirstTime = metadata ? !metadata.existing_integration : false;
      // GLX-1746: "newly added" only applies when a baseline contract is known
      // (originalIntegrations !== undefined). An undefined baseline means no
      // prior contract was loaded → waive setup for everything on renewal.
      const isNewlyAdded =
        originalIntegrations !== undefined && !originalIntegrations.includes(integrationValue);
      const shouldWaiveSetupFee = values.isRenewal && !isNewlyAdded;
      const pipelineOpportunities = integrationPipelineOpportunities[integrationValue] || 1;
      const setupDiscount = integrationSetupDiscounts[integrationValue] || 0;
      const recurringDiscount = integrationRecurringDiscounts[integrationValue] || 0;
      const isMigration =
        integrationMigrations[integrationValue] || values.integrationMigration || false;

      const setupFeeBase = calculateIntegrationSetupFee(
        integrationValue,
        values.numberOfMembers,
        shouldWaiveSetupFee,
        isMigration,
        0,
        isDedicatedTenant(values.tenantType),
        isFirstTime,
      );

      let setupFee = setupFeeBase;
      let discountedSetupFee: number;
      let totalDiscountedSetupFee: number;

      if (isFirstTime) {
        // For first-time integrations, setupFeeBase is the per-integration fee
        // Multiply by pipelineOpportunities to get total setup fee for all opportunities
        const setupFeePerOpportunity = setupFeeBase;
        const setupFeePerOpportunityAfterDiscount =
          setupFeePerOpportunity * (1 - setupDiscount / 100);
        setupFee = setupFeePerOpportunity;
        discountedSetupFee = setupFeePerOpportunityAfterDiscount;
        totalDiscountedSetupFee = setupFeePerOpportunityAfterDiscount * pipelineOpportunities;
      } else {
        discountedSetupFee = calculateIntegrationSetupFee(
          integrationValue,
          values.numberOfMembers,
          shouldWaiveSetupFee,
          isMigration,
          setupDiscount,
          isDedicatedTenant(values.tenantType),
          isFirstTime,
        );
        totalDiscountedSetupFee = discountedSetupFee;
      }

      const annualFee = calculateIntegrationRecurringFee(
        integrationValue,
        values.numberOfMembers,
        values.govCloud,
        isDedicatedTenant(values.tenantType),
        is4xDataTransfer,
        0,
      );

      const discountedAnnualFee = calculateIntegrationRecurringFee(
        integrationValue,
        values.numberOfMembers,
        values.govCloud,
        isDedicatedTenant(values.tenantType),
        is4xDataTransfer,
        recurringDiscount,
      );

      return {
        name: integration?.name || integrationValue,
        value: integrationValue,
        setupFee,
        discountedSetupFee,
        annualFee,
        discountedAnnualFee,
        totalDiscountedSetupFee,
        total: totalDiscountedSetupFee + discountedAnnualFee,
        isFirstTime,
        shouldWaiveSetupFee,
        pipelineOpportunities,
        setupDiscount,
        recurringDiscount,
        isMigration,
      };
    });
  };

  const integrationDetails = getIntegrationDetails();

  return (
    <Box>
      {/* Member Pricing */}
      <CategorySection
        title="Member Pricing"
        icon={<UsersIcon size={18} />}
        expanded={sectionStates.memberPricing}
        onExpandedChange={() => toggleSection('memberPricing')}
      >
        <FormulaCard
          title="Member License Cost"
          icon={<CreditCardIcon size={18} />}
          isActive={true}
          formula={
            <Box>
              <span style={{ color: theme.palette.primary.main }}>Members</span> ×{' '}
              <span style={{ color: theme.palette.primary.main }}>Price</span> × (1 -{' '}
              <span style={{ color: theme.palette.warning.main }}>Discount%</span>) ×{' '}
              <span style={{ color: theme.palette.info.main }}>Duration</span>
            </Box>
          }
          steps={[
            { label: 'Base price per member', value: `${formatCurrency(basePrice)}/month` },
            {
              label: `Discount applied (${values.discountOnMemberPrice.toFixed(1)}%)`,
              value: `${formatCurrency(discountedPrice)}/month`,
            },
            {
              label: `Monthly cost (${values.numberOfMembers} members)`,
              value: formatCurrency(memberCostMonthly),
            },
            {
              label: `Total (${values.subscriptionDuration} months)`,
              value: formatCurrency(memberCostTotal),
              highlight: true,
            },
          ]}
          result={formatCurrency(summary?.totalMemberCostYearly ?? memberCostTotal)}
          note={
            values.planType === PlanType.MOSAIC_ENTERPRISE
              ? 'Enterprise plan applies 1.5× multiplier to base prices'
              : values.planType === PlanType.BASE
                ? 'Base has fixed $9.99/member pricing'
                : undefined
          }
        />

        <FormulaCard
          title="Internal Contractors"
          icon={<UsersIcon size={18} />}
          isActive={values.numberOfInternalContractors > 0}
          formula={
            <Box>
              <span style={{ color: theme.palette.primary.main }}>Count</span> × $
              {PRICING_CONSTANTS.INTERNAL_CONTRACTOR_COST} ×{' '}
              <span style={{ color: theme.palette.info.main }}>Duration</span>
            </Box>
          }
          steps={[
            { label: 'Number of contractors', value: `${values.numberOfInternalContractors}` },
            {
              label: 'Rate per contractor',
              value: `$${PRICING_CONSTANTS.INTERNAL_CONTRACTOR_COST}/month`,
            },
            {
              label: `Total (${values.subscriptionDuration} months)`,
              value: formatCurrency(
                values.numberOfInternalContractors *
                  PRICING_CONSTANTS.INTERNAL_CONTRACTOR_COST *
                  values.subscriptionDuration,
              ),
              highlight: true,
            },
          ]}
          result={formatCurrency(
            summary?.internalContractorsTotal ??
              values.numberOfInternalContractors *
                PRICING_CONSTANTS.INTERNAL_CONTRACTOR_COST *
                values.subscriptionDuration,
          )}
        />

        <FormulaCard
          title="Guests"
          icon={<UsersIcon size={18} />}
          isActive={values.numberOfGuests > 0}
          formula={
            <Box>
              <span style={{ color: theme.palette.primary.main }}>Count</span> × $
              {PRICING_CONSTANTS.GUEST_COST} ×{' '}
              <span style={{ color: theme.palette.info.main }}>Duration</span>
            </Box>
          }
          steps={[
            { label: 'Number of guests', value: `${values.numberOfGuests}` },
            { label: 'Rate per guest', value: `$${PRICING_CONSTANTS.GUEST_COST}/month` },
            {
              label: `Total (${values.subscriptionDuration} months)`,
              value: formatCurrency(
                values.numberOfGuests * PRICING_CONSTANTS.GUEST_COST * values.subscriptionDuration,
              ),
              highlight: true,
            },
          ]}
          result={formatCurrency(
            summary?.guestsTotal ??
              values.numberOfGuests * PRICING_CONSTANTS.GUEST_COST * values.subscriptionDuration,
          )}
        />

        <FormulaCard
          title="External Contractors"
          icon={<UsersIcon size={18} />}
          isActive={values.numberOfExternalContractors > 0}
          formula={
            <Box>
              <span style={{ color: theme.palette.primary.main }}>Count</span> × $
              {PRICING_CONSTANTS.EXTERNAL_CONTRACTOR_COST} ×{' '}
              <span style={{ color: theme.palette.info.main }}>Duration</span>
            </Box>
          }
          steps={[
            { label: 'Number of ext. contractors', value: `${values.numberOfExternalContractors}` },
            {
              label: 'Rate per ext. contractor',
              value: `$${PRICING_CONSTANTS.EXTERNAL_CONTRACTOR_COST}/month`,
            },
            {
              label: `Total (${values.subscriptionDuration} months)`,
              value: formatCurrency(
                values.numberOfExternalContractors *
                  PRICING_CONSTANTS.EXTERNAL_CONTRACTOR_COST *
                  values.subscriptionDuration,
              ),
              highlight: true,
            },
          ]}
          result={formatCurrency(
            summary?.externalContractorsTotal ??
              values.numberOfExternalContractors *
                PRICING_CONSTANTS.EXTERNAL_CONTRACTOR_COST *
                values.subscriptionDuration,
          )}
        />
      </CategorySection>

      {/* Platform Fees */}
      <CategorySection
        title="Platform & Onboarding"
        icon={<CreditCardIcon size={18} />}
        expanded={sectionStates.platformOnboarding}
        onExpandedChange={() => toggleSection('platformOnboarding')}
      >
        <FormulaCard
          title="Platform Fee"
          icon={<CreditCardIcon size={18} />}
          isActive={true}
          formula={
            <Box>
              Tier-based annual fee × (1 -{' '}
              <span style={{ color: theme.palette.warning.main }}>Discount%</span>)
              {values.isItCustomer && ' × 50% (IT Customer)'}
            </Box>
          }
          steps={[
            {
              label: `Your tier (${values.numberOfMembers} members)`,
              value: `Tier ${getPlatformTierIndex(values.numberOfMembers) + 1}`,
            },
            { label: 'Base platform fee', value: formatCurrency(values.platformFee) },
            {
              label: `After discount (${values.discountOnPlatformFee}%)`,
              value: formatCurrency(values.platformFee * (1 - values.discountOnPlatformFee / 100)),
              highlight: true,
            },
          ]}
          result={formatCurrency(
            summary?.platformFeeYearly ??
              values.platformFee * (1 - values.discountOnPlatformFee / 100),
          )}
          note={
            values.isItCustomer ? 'IT Customer receives 50% discount on platform fee' : undefined
          }
        >
          <TierVisual
            title="Your Platform Fee Tier"
            tiers={platformTiers}
            currentTierIndex={getPlatformTierIndex(values.numberOfMembers)}
          />
        </FormulaCard>

        <FormulaCard
          title="Onboarding Fee"
          icon={<UsersIcon size={18} />}
          isActive={!values.isRenewal}
          formula={
            values.isRenewal ? (
              <Box>
                <span style={{ color: theme.palette.error.main }}>$0 (Renewal)</span>
              </Box>
            ) : (
              <Box>
                <span style={{ color: theme.palette.primary.main }}>Groups</span> × $6,000 × (1 -{' '}
                <span style={{ color: theme.palette.warning.main }}>Discount%</span>)
              </Box>
            )
          }
          steps={
            values.isRenewal
              ? [{ label: 'Renewal pricing', value: '$0.00', highlight: true }]
              : [
                  { label: 'Onboarding groups', value: `${values.numberOfOnboardingGroups}` },
                  { label: 'Rate per group', value: '$6,000' },
                  { label: 'Base fee', value: formatCurrency(values.onboardingFee) },
                  {
                    label: `After discount (${values.discountOnOnboardingFee}%)`,
                    value: formatCurrency(
                      summary?.onboardingFeeAfterDiscount ??
                        values.onboardingFee * (1 - values.discountOnOnboardingFee / 100),
                    ),
                    highlight: true,
                  },
                ]
          }
          result={formatCurrency(
            summary?.onboardingFeeAfterDiscount ??
              (values.isRenewal
                ? 0
                : values.onboardingFee * (1 - values.discountOnOnboardingFee / 100)),
          )}
          note={
            values.isRenewal
              ? 'Onboarding fee is waived for renewals. Teams have already completed onboarding.'
              : '1 group supports up to 200 members. Add a group when exceeding 150 members in current group.'
          }
        />
      </CategorySection>

      {/* Server Upgrades */}
      <ServerUpgradesSection
        values={values}
        summary={summary}
        theme={theme}
        isolatedDbTiers={isolatedDbTiers}
        getIsolatedDbTierIndex={getIsolatedDbTierIndex}
        expanded={sectionStates.serverUpgrades}
        onExpandedChange={() => toggleSection('serverUpgrades')}
      />

      {/* Add-ons */}
      <AddonsSection
        values={values}
        summary={summary}
        theme={theme}
        ssoTiers={ssoTiers}
        getSsoTierIndex={getSsoTierIndex}
        discountedPrice={discountedPrice}
        memberCostTotal={memberCostTotal}
        expanded={sectionStates.addons}
        onExpandedChange={() => toggleSection('addons')}
      />

      {/* Integrations */}
      {integrationDetails.length > 0 && (
        <CategorySection
          title="Integrations"
          icon={<LinkIcon size={18} />}
          expanded={sectionStates.integrations}
          onExpandedChange={() => toggleSection('integrations')}
        >
          {integrationDetails.map((integration) => (
            <FormulaCard
              key={integration.value}
              title={integration.name}
              icon={<LinkIcon size={18} />}
              isActive={true}
              formula={
                <Box>
                  Setup Fee{integration.shouldWaiveSetupFee ? ' (Waived for Renewal)' : ''} + Annual
                  Fee
                  {integration.isMigration && ' (Migration)'}
                </Box>
              }
              steps={[
                {
                  label: 'Setup fee (one-time)',
                  value: integration.shouldWaiveSetupFee
                    ? '$0.00 (waived)'
                    : formatCurrency(integration.setupFee),
                },
                ...(integration.setupDiscount > 0
                  ? [
                      {
                        label: `Setup after discount (${integration.setupDiscount}%)`,
                        value: formatCurrency(integration.discountedSetupFee),
                      },
                    ]
                  : []),
                ...(integration.isFirstTime && integration.pipelineOpportunities > 1
                  ? [
                      {
                        label: `Total setup (${integration.pipelineOpportunities} opportunities)`,
                        value: formatCurrency(integration.totalDiscountedSetupFee),
                      },
                    ]
                  : []),
                {
                  label: 'Annual recurring fee',
                  value: formatCurrency(integration.annualFee),
                },
                ...(integration.recurringDiscount > 0
                  ? [
                      {
                        label: `Annual after discount (${integration.recurringDiscount}%)`,
                        value: formatCurrency(integration.discountedAnnualFee),
                      },
                    ]
                  : []),
                {
                  label: 'Total',
                  value: formatCurrency(integration.total),
                  highlight: true,
                },
              ]}
              result={formatCurrency(integration.total)}
              note={
                integration.isFirstTime
                  ? 'First-time integration - setup fee applies per account'
                  : integration.shouldWaiveSetupFee
                    ? 'Renewal - setup fee waived for existing integration'
                    : undefined
              }
            />
          ))}
        </CategorySection>
      )}
    </Box>
  );
};

export default PricingCalculatorFormulas;
